import React from 'react';
import type { Todo } from '@techgitter/contracts';
import { Chip } from '../chip';
import './FollowUpTicketSummary.css';

/**
 * Read-only summary of the ticket a follow-up to-do raises on its scheduled
 * date (template follow-ups — sm4rt-api `FollowUpRaiser`). What it will charge
 * was fixed when the source ticket was saved; a No charge line goes to
 * RepairShopr at $0. Re-date the to-do to move it, set it to Deferred or
 * Blocked to hold it, mark it Done or delete it to cancel it.
 */

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

export const FollowUpTicketSummary: React.FC<{ todo: Todo }> = ({ todo }) => {
  const payload = todo.ticketPayload;
  const raised = !!todo.ticketMessageId;

  return (
    <div className="follow-up-ticket">
      <div className="follow-up-ticket__header">
        <span className="follow-up-ticket__title">Raises a ticket</span>
        {raised ? (
          <Chip tone="success">
            {todo.ticketNumber ? `Raised — #${todo.ticketNumber}` : 'Raised'}
          </Chip>
        ) : (
          <Chip tone="info">On the scheduled date</Chip>
        )}
      </div>

      {todo.ticketError && !raised && (
        <div className="side-panel__alert side-panel__alert--error">
          Last attempt failed — retried on the next run: {todo.ticketError}
        </div>
      )}

      {payload ? (
        <>
          <div className="follow-up-ticket__subject">{payload.subject}</div>
          {payload.lineItems.length === 0 ? (
            <div className="follow-up-ticket__empty">No charge lines</div>
          ) : (
            payload.lineItems.map((line, i) => (
              <div key={i} className="follow-up-ticket__line">
                <span className="follow-up-ticket__sku">{line.sku}</span>
                <span className="follow-up-ticket__qty">
                  {line.quantity} × {money(line.unitCost)}
                </span>
                <span className="follow-up-ticket__total">
                  {line.nonChargeable ? (
                    <Chip tone="neutral">No charge</Chip>
                  ) : (
                    money(line.quantity * line.unitCost)
                  )}
                </span>
              </div>
            ))
          )}
        </>
      ) : (
        <div className="follow-up-ticket__empty">The ticket details are unreadable.</div>
      )}

      {!raised && (
        <p className="follow-up-ticket__hint">
          Change the scheduled date to move it. Deferred or Blocked holds it; Done or deleting it cancels it.
        </p>
      )}
    </div>
  );
};
